import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOptionalAuth } from '@/contexts/AuthContext';

const STORE_URLS = {
  android: 'https://play.google.com/store/apps/details?id=com.careos.app',
  ios: 'https://apps.apple.com/app/careos/id6747797186',
} as const;

const SNOOZE_STORAGE_KEY = 'app_version_update_snooze';
const SNOOZE_MS = 24 * 60 * 60 * 1000;

interface SnoozeRecord {
  version: string;
  dismissedAt: number;
}

function readSnooze(): SnoozeRecord | null {
  try {
    const raw = localStorage.getItem(SNOOZE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SnoozeRecord>;
    if (typeof parsed?.version !== 'string' || typeof parsed?.dismissedAt !== 'number') return null;
    return { version: parsed.version, dismissedAt: parsed.dismissedAt };
  } catch {
    return null;
  }
}

/**
 * Compares two semver strings (major.minor.patch).
 * Missing segments are treated as 0 ("1.1" === "1.1.0").
 * Pre-release/build suffixes are ignored ("1.2.3-beta.1" === "1.2.3").
 * Returns -1 when a < b, 0 when equal, 1 when a > b.
 */
export function compareSemver(a: string, b: string): number {
  const parse = (v: string): [number, number, number] => {
    const core = String(v ?? '').trim().replace(/^v/i, '').split(/[-+]/)[0];
    const parts = core.split('.').map((n) => {
      const parsed = parseInt(n, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    });
    return [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0];
  };

  const [aMaj, aMin, aPatch] = parse(a);
  const [bMaj, bMin, bPatch] = parse(b);

  if (aMaj !== bMaj) return aMaj < bMaj ? -1 : 1;
  if (aMin !== bMin) return aMin < bMin ? -1 : 1;
  if (aPatch !== bPatch) return aPatch < bPatch ? -1 : 1;
  return 0;
}

const toVersion = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim() !== '') return value.trim();
  return null;
};

export interface AppVersionCheckResult {
  /** True when the installed version is below the mandatory minimum */
  updateRequired: boolean;
  /** True when the installed version is below the recommended version (but above minimum) */
  updateRecommended: boolean;
  /** URL to redirect the user to the appropriate store */
  storeUrl: string;
  /** Dismiss the recommended update for this session */
  dismissRecommended: () => void;
}

/**
 * Checks whether the currently installed app version needs updating.
 * - Returns early with all-false state on web/browser platforms.
 * - Fetches the version config only once the user is authenticated (RLS requires it),
 *   using a dedicated query so it is never served from a stale anonymous cache.
 * - Respects a session-level dismissal for non-mandatory updates.
 */
export function useAppVersionCheck(): AppVersionCheckResult {
  const auth = useOptionalAuth();
  const userId = auth?.user?.id ?? null;

  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform() as 'android' | 'ios' | 'web';
  const storeUrl = platform === 'android' ? STORE_URLS.android : STORE_URLS.ios;

  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [snooze, setSnooze] = useState<SnoozeRecord | null>(() => readSnooze());

  const { data: config } = useQuery({
    queryKey: ['app-version-config', platform, userId],
    enabled: isNative && !!userId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const minKey = `app_min_version_${platform}`;
      const recKey = `app_recommended_version_${platform}`;
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', [minKey, recKey]);

      if (error) {
        console.warn('[AppVersionCheck] Failed to load version config');
        return { min: null, recommended: null };
      }

      const byKey = new Map((data ?? []).map((row) => [row.key, row.value]));
      return {
        min: toVersion(byKey.get(minKey)),
        recommended: toVersion(byKey.get(recKey)),
      };
    },
  });

  useEffect(() => {
    if (!isNative) return;

    import('@capacitor/app').then(({ App }) => {
      App.getInfo()
        .then((info) => setCurrentVersion(info.version))
        .catch(() => {
          // Silently fail — no version info means no enforcement
        });
    });
  }, [isNative]);

  const minVersion = config?.min ?? null;
  const recommendedVersion = config?.recommended ?? null;

  const dismissRecommended = () => {
    if (!recommendedVersion) return;
    const record: SnoozeRecord = { version: recommendedVersion, dismissedAt: Date.now() };
    setSnooze(record);
    try {
      localStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify(record));
    } catch {
      // storage not available — in-memory dismissed state still works
    }
  };

  if (!isNative || !currentVersion) {
    return { updateRequired: false, updateRecommended: false, storeUrl, dismissRecommended };
  }

  // Snoozed only while it refers to the same recommended version and is under 24h old.
  const isSnoozed =
    snooze != null &&
    recommendedVersion != null &&
    snooze.version === recommendedVersion &&
    Date.now() - snooze.dismissedAt < SNOOZE_MS;

  const updateRequired = minVersion != null && compareSemver(currentVersion, minVersion) < 0;
  const updateRecommended =
    !updateRequired &&
    !isSnoozed &&
    recommendedVersion != null &&
    compareSemver(currentVersion, recommendedVersion) < 0;

  return { updateRequired, updateRecommended, storeUrl, dismissRecommended };
}
